import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { ComingSoonDialog } from '@/components/ui/ComingSoonDialog'
import { useApi } from '@/hooks/useAppApi'
import { usePermissions } from '@/hooks/usePermissions'
import { useLogout } from '@/platform/auth/LogoutButton'
import {
  User,
  Settings,
  HelpCircle,
  FileText,
  MessageSquarePlus,
  LogOut,
  ChevronDown,
  Eye,
  EyeOff,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePricingToggle } from '@/platform/pricing/PricingToggleContext'

/**
 * User info display within the dropdown menu
 */
const UserInfo = (): JSX.Element => {
  const { user } = useApi()

  if (!user) return <></>

  // Get initials from name for avatar fallback
  const initials = user.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U'

  return (
    <div className="flex items-center gap-3 px-2 py-3 border-b">
      <Avatar className="h-10 w-10">
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{user.name || 'User'}</div>
        <div className="text-xs text-muted-foreground truncate">{user.email || ''}</div>
      </div>
    </div>
  )
}

interface MenuItemProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  onClick: () => void
  variant?: 'default' | 'destructive'
}

/**
 * Individual menu item component
 */
const MenuItem = ({ icon: Icon, label, onClick, variant = 'default' }: MenuItemProps) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-2 py-2 text-sm rounded-md transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground focus:outline-none',
        variant === 'destructive' && 'text-destructive hover:text-destructive'
      )}
      role="menuitem"
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1 text-left">{label}</span>
    </button>
  )
}

type DialogType = 'profile' | 'case' | 'docs' | 'help' | null

/**
 * Enhanced user menu dropdown with common navigation items
 *
 * Includes:
 * - User profile information
 * - Update My Profile
 * - Settings/Admin (conditional on permissions)
 * - Open Case
 * - Documentation
 * - Help/Support
 * - Logout
 *
 * Features not yet implemented show a "Coming Soon" dialog.
 */
export function UserMenuDropdown(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [activeDialog, setActiveDialog] = useState<DialogType>(null)
  const navigate = useNavigate()
  const { hasAnyPermission } = usePermissions()
  const hasAdminAccess = hasAnyPermission(['admin:*'])
  const { triggerLogout, isLoading: isLoggingOut } = useLogout()
  const { user } = useApi()
  const { visible: pricingVisible, toggle: togglePricing } = usePricingToggle()

  // Get display name for the trigger button
  const displayName = user?.name?.split(' ')[0] || 'Account'

  // Close dropdown when clicking outside
  const handleBackdropClick = () => {
    setIsOpen(false)
  }

  // Handle navigation items
  const handleProfile = () => {
    setIsOpen(false)
    setActiveDialog('profile')
    // TODO: When profile page exists, use: navigate('/profile')
  }

  const handleAdmin = () => {
    setIsOpen(false)
    navigate('/admin')
  }

  const handleOpenCase = () => {
    setIsOpen(false)
    setActiveDialog('case')
    // TODO: When case management exists, use: navigate('/support/cases/new')
  }

  const handleDocs = () => {
    setIsOpen(false)
    setActiveDialog('docs')
    // TODO: When docs exist, use: window.open('https://autoquotes-fes.com/aq-cpq/', '_blank')
  }

  const handleHelp = () => {
    setIsOpen(false)
    setActiveDialog('help')
    // TODO: When help center exists, use: window.open('https://help.revalize.com', '_blank')
  }

  const handlePricingToggle = () => {
    setIsOpen(false)
    togglePricing()
  }

  const handleLogout = async () => {
    setIsOpen(false)
    await triggerLogout()
  }

  return (
    <>
      <div className="relative">
        {/* Trigger Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2"
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <Avatar className="h-6 w-6">
            <AvatarFallback className="text-xs">
              {user?.name
                ? user.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                : 'U'}
            </AvatarFallback>
          </Avatar>
          <span className="hidden sm:inline">{displayName}</span>
          <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
        </Button>

        {/* Dropdown Menu */}
        {isOpen && (
          <>
            {/* Backdrop for closing dropdown */}
            <div className="fixed inset-0 z-40" onClick={handleBackdropClick} />

            {/* Menu Content */}
            <div
              className="absolute right-0 top-full mt-2 z-50 min-w-[240px] bg-popover border rounded-md shadow-lg"
              role="menu"
              aria-orientation="vertical"
            >
              <div className="p-2">
                {/* User Info */}
                <UserInfo />

                {/* Menu Items */}
                <div className="py-2 space-y-1">
                  <MenuItem
                    icon={User}
                    label="Update My Profile"
                    onClick={handleProfile}
                  />

                  {hasAdminAccess && (
                    <MenuItem
                      icon={Settings}
                      label="Settings / Admin"
                      onClick={handleAdmin}
                    />
                  )}

                  <MenuItem
                    icon={pricingVisible ? EyeOff : Eye}
                    label={pricingVisible ? 'Hide pricing' : 'Show pricing'}
                    onClick={handlePricingToggle}
                  />

                  {/* Separator */}
                  <div className="border-t my-2" />

                  <MenuItem
                    icon={MessageSquarePlus}
                    label="Open Case"
                    onClick={handleOpenCase}
                  />

                  <MenuItem
                    icon={FileText}
                    label="Documentation"
                    onClick={handleDocs}
                  />

                  <MenuItem
                    icon={HelpCircle}
                    label="Help / Support"
                    onClick={handleHelp}
                  />

                  {/* Separator */}
                  <div className="border-t my-2" />

                  <MenuItem
                    icon={LogOut}
                    label={isLoggingOut ? 'Signing Out...' : 'Logout'}
                    onClick={handleLogout}
                    variant="destructive"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Coming Soon Dialogs */}
      <ComingSoonDialog
        open={activeDialog === 'profile'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        featureName="User Profile"
        description="Update your profile information, preferences, and settings."
      />

      <ComingSoonDialog
        open={activeDialog === 'case'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        featureName="Case Management"
        description="Create and track support cases. Contact your system administrator for immediate assistance."
      />

      <ComingSoonDialog
        open={activeDialog === 'docs'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        featureName="Documentation"
        description="Access comprehensive product documentation, guides, and tutorials."
      />

      <ComingSoonDialog
        open={activeDialog === 'help'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        featureName="Help Center"
        description="Get help with common questions, troubleshooting, and best practices."
      />
    </>
  )
}
